/* eslint-disable unicorn/no-useless-undefined */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTestId } from '../../hooks/useTestId/useTestId';
import { IModalProps, useModalWrapperCenteredCss, useModalWrapperCss } from '../Modal/Modal';
import { Overlay } from '../../components/Overlay/Overlay';
import { getElement, removeBodyOverflow, scrollToElementNotInViewport, toggleClassnameOnElement } from '../../common/domUtils';
import usePopper from 'react-overlays/usePopper';
import { StepPopup } from './StepPopup';
import { GLOBAL_CLASSNAMES } from '../../themes/EoraThemeProvider';
import { IStepConfig, IStep, EVENTS, MODIFIERS_HORIZ, MODIFIERS_VERT } from './tourModal.common';
import styled, { css } from 'styled-components';
import ReactModal from 'react-overlays/Modal';

type ActiveStep = IStep & { position: number };
export interface ITourModalCallbackArgs {
  step?: ActiveStep;
  isError?: boolean;
  msg?: string;
}

export interface ITourModalProps extends Pick<IModalProps, 'onShow'> {
  steps: IStep[];
  run?: boolean;
  startIndex?: number;
  stepConfig?: IStepConfig;

  callback?: (arg?: ITourModalCallbackArgs) => void;

  // Main events get their own callback fn
  onClose?: (arg?: ITourModalCallbackArgs) => void;
  onTourComplete?: (arg?: ITourModalCallbackArgs) => void;
}

enum NAVIGATION {
  Back = 'back',
  Forward = 'forward',
  Reload = 'reload'
}

const CENTERED = 'centered';

const ModalWrapper = styled(ReactModal)<{ centered?: string }>`
  ${useModalWrapperCss};
  ${({ centered }) =>
    centered === CENTERED
      ? useModalWrapperCenteredCss
      : css`
          width: 100%;
        `}
`;

const toggleGlobalClassnames = (active?: ActiveStep, force?: boolean) => {
  if (active?.asModal) {
    return;
  }

  active?.addBg && toggleClassnameOnElement(active?.target, GLOBAL_CLASSNAMES.TOP_OVERLAY_TARGET_BG, force);
  toggleClassnameOnElement(active?.target, GLOBAL_CLASSNAMES.TOP_OVERLAY, force);
};

export const TourModal: React.FC<ITourModalProps> = ({ steps, stepConfig, run, onClose, onTourComplete, callback, onShow, startIndex = 0, ...rest }) => {
  const { generateTestId } = useTestId(rest, 'TourModal');

  const ref = useRef<HTMLDivElement>(null);

  const [show, setShow] = useState(run);
  const [activeStep, setActiveStep] = useState<ActiveStep | undefined>();
  const [started, setStarted] = useState(run);

  const computedSteps = useMemo(() => steps.map((currentStep, position) => ({ ...currentStep, position })), [steps]);

  const resetTour = (arg: ITourModalCallbackArgs) => {
    toggleGlobalClassnames(activeStep, false);
    setActiveStep(undefined);
    setShow(false);
    setStarted(false);
    onTourComplete?.(arg);
    callback?.(arg);
  };

  const navigateTour = (direction?: NAVIGATION) => {
    toggleGlobalClassnames(activeStep, false);

    const goBack = direction === NAVIGATION.Back;
    const reload = direction === NAVIGATION.Reload;
    const pos = activeStep ? activeStep.position : startIndex - 1;
    // eslint-disable-next-line unicorn/no-nested-ternary
    const active = pos + (reload ? 0 : goBack ? -1 : 1);

    if (!getElement(computedSteps[active].target)) {
      return resetTour({
        msg: EVENTS.ERROR_TARGET_NOT_FOUND,
        step: computedSteps[active],
        isError: true
      });
    }

    const mergedStep = { ...stepConfig, ...computedSteps[active] };

    setActiveStep(mergedStep);
    callback?.({ msg: direction === NAVIGATION.Back ? EVENTS.STEP_BEFORE : EVENTS.STEP_AFTER, step: mergedStep });

    toggleGlobalClassnames(mergedStep, true);
  };

  useEffect(() => {
    if (activeStep !== undefined && computedSteps.length - 1 >= startIndex) {
      // when screen is resized and modal repositioned, this helps to apply styles to the new target element
      navigateTour(NAVIGATION.Reload);
    }
  }, [steps]);

  const activeElement = useMemo(() => getElement(activeStep?.target), [activeStep?.target, activeStep?.placement, activeStep?.size]);

  useEffect(() => {
    if (started) {
      callback?.({
        msg: EVENTS.TOUR_START
      });
    }
  }, [started]);

  useEffect(() => {
    scrollToElementNotInViewport(activeStep?.target, activeStep?.scrollToTop);
  }, [activeElement, activeStep]);

  useEffect(() => {
    if (show) {
      navigateTour();
    }
  }, [show]);

  useEffect(() => {
    if (run && !started) {
      setStarted(true);
    }
    setShow(run);
  }, [run]);

  const handleOnModalHide = () => {
    onClose && toggleGlobalClassnames(activeStep, false);

    if (activeStep?.position === computedSteps.length - 1 && onClose) {
      resetTour({ msg: EVENTS.TOUR_END });
    }
    const args = { msg: EVENTS.TOUR_CLOSE, step: activeStep };

    onClose?.(args);
    callback?.(args);
  };

  const placement = activeStep?.placement;

  const popperState = usePopper(activeElement, ref.current, {
    placement: placement,
    strategy: 'fixed',
    modifiers: placement === undefined || placement.includes('top') || placement.includes('bottom') ? MODIFIERS_HORIZ : MODIFIERS_VERT
  });

  // popperState returns stale placement, assign placement above directly
  const { styles } = popperState;

  const onNextClickHandler = () => navigateTour();
  const onBackClickHandler = () => navigateTour(NAVIGATION.Back);

  const onLastClickHandler = () => {
    if (onClose) {
      resetTour({ msg: EVENTS.TOUR_END });
    }
  };

  return (
    <ModalWrapper
      show={show}
      onShow={onShow ?? removeBodyOverflow}
      centered={activeStep?.asModal ? CENTERED : undefined} // Note: passed as string to avoid DOM error
      renderBackdrop={(props: any) => <Overlay {...props} blur={activeStep?.blur ?? false} />}
      {...generateTestId()}
      {...rest}
      onHide={handleOnModalHide}
    >
      <StepPopup
        ref={ref}
        {...activeStep}
        styles={styles}
        stepsCount={computedSteps.length}
        onBackClick={onBackClickHandler}
        onLastClick={onLastClickHandler}
        onNextClick={onNextClickHandler}
        placement={placement}
        onClose={handleOnModalHide}
      />
    </ModalWrapper>
  );
};
